import React, { useState, useEffect, useRef } from "react";
import ToastPopup from "./ToastPopup";
import { EditChecklistModalProps, createToastMessage, ToastMessage } from "../Interfaces/Interface";
import { apiCall } from "../main/Apicall";
const EditChecklistModal: React.FC<EditChecklistModalProps> = ({ isOpen, onClose, editableDetails, setEditableDetails, onSave }) => {
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [controlOptions, setControlOptions] = useState<{ value: string; description: string }[]>([]);
  const [loadingControls, setLoadingControls] = useState<boolean>(false);
  const [prefix, setPrefix] = useState<string>("");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const extractPrefixFromValue = (scopeValue: string): string => {
    const cleanValue = scopeValue.replace(/\s*Controls\s*/i, "");
    const firstWord = cleanValue.trim().split(/\s+/)[0];
    return `${firstWord}_`;
  };
  useEffect(() => {
    const fetchControlTypes = async () => {
      setLoadingControls(true);
      try {
        const response = await apiCall("get", "/api/checklist/templates/controlEnums");
        setControlOptions(response);
      } catch (error) {
        setMessage(createToastMessage("Failed to load control types", "error", 3000));
      } finally {
        setLoadingControls(false);
      }
    };
    if (isOpen) {
      fetchControlTypes();
    }
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen) return;
    if (!editableDetails || editableDetails.isIntegratedChecklist || !editableDetails.scope) {
      setPrefix("");
      return;
    }
    const newPrefix = extractPrefixFromValue(editableDetails.scope);
    setPrefix(newPrefix);
    if (editableDetails.templateName?.startsWith(newPrefix)) {
      return;
    }
    setEditableDetails((prev: any) => {
      if (!prev) return prev;
      if (!prev.templateName) {
        return { templateName: newPrefix };
      }
      const suffix = prev.templateName.includes("_") ? prev.templateName.split("_").slice(1).join("_") : "";
      return { ...prev, templateName: suffix ? newPrefix + suffix : newPrefix };
    });
  }, [editableDetails, isOpen, setEditableDetails]);
  if (!isOpen) return null;
  const handleCheckboxChange = (controlValue: string) => {
    const existingControls = editableDetails?.controls || [];
    const updatedControls = existingControls.includes(controlValue)
      ? existingControls.filter((c: string) => c !== controlValue)
      : [...existingControls, controlValue];
    setEditableDetails((prev: any) => ({
      ...prev, controls: updatedControls
    }));
  };
  const handleScopeChange = (scopeValue: string) => {
    const newPrefix = extractPrefixFromValue(scopeValue);
    setPrefix(newPrefix);
    setEditableDetails((prev: any) => {
      if (!prev) return prev;
      let updatedName = prev.templateName || "";
      if (!updatedName.startsWith(newPrefix)) {
        const suffix = updatedName.includes("_") ? updatedName.split("_").slice(1).join("_") : "";
        updatedName = suffix ? newPrefix + suffix : newPrefix;
      }
      return { ...prev, scope: scopeValue, templateName: updatedName };
    });
  };
  const handleNameChange = (value: string) => {
    if (editableDetails?.isIntegratedChecklist) {
      setEditableDetails((prev: any) => ({
        ...prev, templateName: value
      }));
      return;
    }
    if (!editableDetails?.scope) {
      setMessage(createToastMessage("Please select one scope first", "warning", 3000));
      return;
    }
    if (value === "") {
      setEditableDetails((prev: any) => ({
        ...prev, templateName: prefix
      }));
    } else if (value.startsWith(prefix)) {
      setEditableDetails((prev: any) => ({
        ...prev, templateName: value
      }));
    } else {
      const suffix = value.replace(prefix, "").replace(/^[^_]*_?/, "");
      setEditableDetails((prev: any) => ({
        ...prev, templateName: prefix + suffix
      }));
      setMessage(createToastMessage("Scope prefix cannot be removed or modified", "warning", 2000));
    }
  };
  const handleSaveWithValidation = () => {
    const name = editableDetails?.templateName?.trim();
    const desc = editableDetails?.description?.trim();
    if (!name || !desc) {
      setMessage(createToastMessage("Please fill all fields", "warning", 3000));
      return;
    }
    if ((editableDetails?.templateName || "").length > 60) {
      setMessage(createToastMessage("Checklist Name cannot exceed 60 characters", "warning", 3000));
      return;
    }
    if (editableDetails?.isIntegratedChecklist) {
      if (!editableDetails.controls || editableDetails.controls.length < 2) {
        setMessage(createToastMessage("Please select at least two controls for an integrated checklist", "warning", 3000));
        return;
      }
    } else {
      if (!editableDetails?.scope) {
        setMessage(createToastMessage("Please select one scope for a normal checklist", "warning", 3000));
        return;
      }
      if (!editableDetails.templateName?.startsWith(prefix)) {
        setMessage(createToastMessage("Checklist name must start with the scope prefix", "warning", 3000));
        return;
      }
      if (editableDetails.templateName.length <= prefix.length) {
        setMessage(createToastMessage("Please add a name after the prefix", "warning", 3000));
        return;
      }
    }
    onSave();
  };
  const handleBeforeInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    if (!editableDetails?.isIntegratedChecklist && prefix && editableDetails?.scope) {
      const input = e.currentTarget;
      const cursorPosition = input.selectionStart || 0;
      const inputEvent = e as any;
      if (
        inputEvent.inputType === "deleteContentBackward" || inputEvent.inputType === "deleteContentForward" || inputEvent.inputType === "deleteByCut"
      ) {
        if (cursorPosition <= prefix.length) {
          e.preventDefault();
          setMessage(createToastMessage("Scope prefix cannot be deleted", "warning", 2000));
        }
      }
    }
  };
  const handleSelect = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    if (!editableDetails?.isIntegratedChecklist && prefix && editableDetails?.scope) {
      const input = e.currentTarget;
      if (input.selectionStart !== null && input.selectionStart < prefix.length) {
        input.setSelectionRange(prefix.length, input.selectionEnd ?? prefix.length);
      }
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!editableDetails?.isIntegratedChecklist && prefix && editableDetails?.scope) {
      const input = e.currentTarget;
      const cursorPosition = input.selectionStart || 0;
      if (
        (e.key === "Backspace" && cursorPosition <= prefix.length) || (e.key === "Delete" && cursorPosition < prefix.length)
      ) {
        e.preventDefault();
        setMessage(createToastMessage("Scope prefix cannot be deleted", "warning", 2000));
      }
      if (e.key === "ArrowLeft" && cursorPosition === prefix.length) {
        e.preventDefault();
      }
      if (e.key === "Home") {
        e.preventDefault();
        input.setSelectionRange(prefix.length, prefix.length);
      }
    }
  };
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!editableDetails?.isIntegratedChecklist && prefix && editableDetails?.scope) {
      const input = e.currentTarget;
      const cursorPosition = input.selectionStart || 0;
      if (cursorPosition < prefix.length) {
        e.preventDefault();
        setMessage(createToastMessage("Cannot paste into the prefix area", "warning", 2000));
      }
    }
  };
  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h3>Edit Checklist Item</h3>
        <label>Checklist Type:
          <input type="text" value={editableDetails?.isIntegratedChecklist ? "Integrated Checklist" : "Normal Checklist"} readOnly disabled />
        </label>
        <label>Check List Name:
          <textarea ref={inputRef} value={editableDetails?.templateName || ""} onChange={(e) => handleNameChange(e.target.value)} onKeyDown={handleKeyDown} onSelect={handleSelect} onBeforeInput={handleBeforeInput} onPaste={handlePaste} disabled={!editableDetails?.isIntegratedChecklist && !editableDetails?.scope} placeholder={!editableDetails?.isIntegratedChecklist ? editableDetails?.scope ? `Enter name after ${prefix}` : "Please select scope first" : "Enter checklist name"} />
        </label>
        <label>Description:
          <textarea value={editableDetails?.description || ""} onChange={(e) => setEditableDetails((prev: any) => ({ ...prev, description: e.target.value }))} />
        </label>
        {editableDetails?.isIntegratedChecklist ? (
          <>
            <label className="controls-edit-label">Select Controls:</label>
            <div className="controls-edit-section">
              {loadingControls ? (
                <div>Loading control types...</div>
              ) : (
                <div className="controls-edit-list">
                  {controlOptions.map((control) => (
                    <label key={control.value} className="control-edit-option">
                      <input type="checkbox" value={control.value} checked={editableDetails?.controls?.includes(control.value) || false} onChange={() => handleCheckboxChange(control.value)} />{control.description} ({control.value})
                    </label>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <label className="controls-edit-label">Scope (Select any one control):</label>
            <div className="controls-edit-section">
              {loadingControls ? (
                <div>Loading control types...</div>
              ) : (
                <div className="controls-edit-list">
                  {controlOptions.map((control) => (
                    <label key={control.value} className="control-edit-option">
                      <input type="radio" name="editNormalChecklistScope" value={control.value} checked={editableDetails?.scope === control.value} onChange={() => handleScopeChange(control.value)} />{control.description} ({control.value})
                    </label>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
        <div className="buttons">
          <button className="cancelbutton" onClick={onClose}>Cancel</button>
          <button className={`savebutton ${editableDetails?.isIntegratedChecklist ? (editableDetails?.controls?.length || 0) < 2 ? "savebutton-disabled" : "" : !editableDetails?.scope ? "savebutton-disabled" : ""}`} onClick={handleSaveWithValidation} disabled={editableDetails?.isIntegratedChecklist ? (editableDetails?.controls?.length || 0) < 2 : !editableDetails?.scope}>Save</button>
        </div>
        {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
      </div>
    </div>
  );
};
export default EditChecklistModal;